/**
 * Accessibility Fixes
 *
 * This script handles multiple accessibility improvements:
 * 1. Adds proper heading roles to paragraph elements styled as headings
 * 2. Adds aria-labels to links that open in new tabs/windows
 * 3. Adds navigation roles to HubSpot cookie banners
 * 4. Adds aria-required to required form fields
 * 5. Improves contrast for HubSpot elements
 * 6. Adds proper landmark roles to header, main, and navigation elements
 * 7. Fixes heading hierarchy by converting problematic headings to paragraphs
 */

(function () {
  "use strict";

  // Configuration: set to true to enable console logging
  const ENABLE_LOGGING = false;

  /**
   * Helper function for logging
   */
  function log(message) {
    if (ENABLE_LOGGING && window.console && typeof console.log === "function") {
      console.log(message);
    }
  }

  /**
   * Fixes heading hierarchy issues by converting problematic headings to paragraphs
   */
  function fixHeadingHierarchy() {
    // Find all heading elements
    const allHeadings = document.querySelectorAll("h1, h2, h3, h4, h5, h6");
    const headingLevels = [];

    // Track heading levels in document order
    allHeadings.forEach((heading) => {
      const level = parseInt(heading.tagName.substring(1));
      headingLevels.push({ element: heading, level: level });
    });

    // Find headings that skip levels or are empty
    let expectedLevel = 1;
    let foundFirstHeading = false;

    headingLevels.forEach((item, index) => {
      const { element, level } = item;

      // Check if heading is empty
      const isEmpty = !element.textContent.trim();

      // Check if this heading skips a level
      let skipsLevel = false;
      if (foundFirstHeading && level > expectedLevel + 1) {
        skipsLevel = true;
      }

      // Convert problematic headings to paragraphs
      if (isEmpty || skipsLevel) {
        // Create a new paragraph element
        const p = document.createElement("p");

        // Copy all attributes
        Array.from(element.attributes).forEach((attr) => {
          p.setAttribute(attr.name, attr.value);
        });

        // Copy content
        p.innerHTML = element.innerHTML;

        // Add the heading class for styling
        const headingClass = `h${level}`;
        if (!p.className.includes(headingClass)) {
          p.className = p.className
            ? `${p.className} ${headingClass}`
            : headingClass;
        }

        // Add accessibility attributes
        p.setAttribute("role", "heading");
        p.setAttribute("aria-level", level.toString());

        // Replace the heading with the paragraph
        element.parentNode.replaceChild(p, element);

        log(
          `Accessibility fix applied: Converted ${
            isEmpty ? "empty" : "level-skipping"
          } <${element.tagName.toLowerCase()}> to <p> with role="heading" aria-level="${level}"`
        );
      } else {
        // Update expected level for next iteration
        if (!foundFirstHeading) {
          foundFirstHeading = true;
          expectedLevel = level;
        }
        expectedLevel = level;
      }
    });
  }

  /**
   * Adds heading role and aria-level to paragraph elements with heading classes
   */
  function fixHeadingAccessibility() {
    // Define heading levels to check
    const headingLevels = ["h1", "h2", "h3", "h4", "h5", "h6"];

    headingLevels.forEach((headingClass, index) => {
      const level = index + 1; // h1 = level 1, h2 = level 2, etc.

      // Find all <p> elements with the heading class
      // Using various selectors to catch different class formats
      const selectors = [
        `p.${headingClass}`, // Exact class match
        `p[class*=" ${headingClass} "]`, // Class with spaces around it
        `p[class^="${headingClass} "]`, // Class at start
        `p[class$=" ${headingClass}"]`, // Class at end
        `p[class="${headingClass}"]`, // Only class
      ];

      selectors.forEach((selector) => {
        try {
          const elements = document.querySelectorAll(selector);

          elements.forEach((element) => {
            // Only add role if it doesn't already have one
            if (!element.hasAttribute("role")) {
              element.setAttribute("role", "heading");
            }

            // Only add aria-level if it doesn't already have one
            if (!element.hasAttribute("aria-level")) {
              element.setAttribute("aria-level", level.toString());
            }

            // Log for debugging
            log(
              `Accessibility fix applied: <p class="${element.className}"> now has role="heading" aria-level="${level}"`
            );
          });
        } catch (e) {
          // Silently handle any selector errors
        }
      });
    });
  }

  /**
   * Adds aria-labels to links that open in new tabs or windows
   */
  function fixExternalLinkAccessibility() {
    // Find all links with target="_blank" or target="_new"
    const externalLinks = document.querySelectorAll(
      'a[target="_blank"], a[target="_new"]'
    );

    externalLinks.forEach((link) => {
      // Check if the link already has an aria-label that mentions "new tab" or "new window"
      const existingAriaLabel = link.getAttribute("aria-label");
      const alreadyHasNewTabInfo =
        existingAriaLabel &&
        (existingAriaLabel.toLowerCase().includes("new tab") ||
          existingAriaLabel.toLowerCase().includes("new window") ||
          existingAriaLabel.toLowerCase().includes("opens in"));

      if (!alreadyHasNewTabInfo) {
        // Get the link text or title
        const linkText =
          link.textContent.trim() ||
          link.getAttribute("title") ||
          link.getAttribute("href") ||
          "Link";

        // Create a descriptive aria-label
        const newAriaLabel = `${linkText} (opens in new tab)`;
        link.setAttribute("aria-label", newAriaLabel);

        // Log for debugging
        log(
          `Accessibility fix applied: Link with target="_blank" now has aria-label="${newAriaLabel}"`
        );
      }
    });
  }

  /**
   * Adds navigation roles to HubSpot cookie confirmation areas
   */
  function fixHubSpotNavigationRoles() {
    // Find HubSpot cookie confirmation areas
    const cookieBannerAreas = document.querySelectorAll(
      "#hs-en-cookie-confirmation-buttons-area, #hs-eu-cookie-confirmation-button-group"
    );

    cookieBannerAreas.forEach((area) => {
      // Only add role if it doesn't already have one
      if (!area.hasAttribute("role")) {
        area.setAttribute("role", "navigation");
        area.setAttribute("aria-label", "Cookie consent options");

        // Log for debugging
        log(
          `Accessibility fix applied: HubSpot cookie banner area now has role="navigation"`
        );
      }
    });
  }

  /**
   * Adds aria-required to required form fields
   */
  function fixRequiredFormFields() {
    // Find all required input fields - be very comprehensive
    const requiredInputs = document.querySelectorAll(
      "input[required], textarea[required], select[required], " +
        "input[aria-required], textarea[aria-required], select[aria-required]"
    );

    requiredInputs.forEach((input) => {
      // Only add aria-required if it doesn't already have it set to "true"
      if (input.getAttribute("aria-required") !== "true") {
        input.setAttribute("aria-required", "true");

        log(
          `Accessibility fix applied: Required field ${
            input.id || input.name || "unknown"
          } now has aria-required="true"`
        );
      }
    });

    // Also check for fields with visual required indicators
    // Look for inputs with required asterisks or labels
    const allInputs = document.querySelectorAll("input, textarea, select");
    allInputs.forEach((input) => {
      // Skip if already handled
      if (
        input.getAttribute("aria-required") === "true" ||
        input.hasAttribute("required")
      ) {
        return;
      }

      // Check if the input has a label with a required indicator
      const inputId = input.id;
      if (inputId) {
        const label = document.querySelector(`label[for="${inputId}"]`);
        if (label) {
          // Check if label contains asterisk or required text
          const labelText = label.textContent;
          const hasRequiredIndicator =
            labelText.includes("*") ||
            labelText.toLowerCase().includes("required") ||
            label.querySelector(
              ".required, .hs-form-required, [class*='required']"
            );

          if (hasRequiredIndicator) {
            input.setAttribute("aria-required", "true");
            log(
              `Accessibility fix applied: Field ${inputId} marked as required based on label indicator`
            );
          }
        }
      }

      // Check if the input is in a container with required class
      const container = input.closest(".required, [class*='required']");
      if (container && !input.getAttribute("aria-required")) {
        input.setAttribute("aria-required", "true");
        log(
          `Accessibility fix applied: Field ${
            input.id || input.name || "unknown"
          } marked as required based on container`
        );
      }
    });
  }

  /**
   * Improves contrast for HubSpot elements
   */
  function fixHubSpotContrast() {
    // Add inline styles to improve contrast for HubSpot cookie banner buttons
    const acceptButton = document.querySelector("#hs-eu-confirmation-button");
    const declineButton = document.querySelector("#hs-eu-decline-button");

    if (acceptButton && !acceptButton.hasAttribute("data-contrast-fixed")) {
      acceptButton.style.color = "#ffffff";
      acceptButton.style.backgroundColor = "#0066cc";
      acceptButton.setAttribute("data-contrast-fixed", "true");

      log("Accessibility fix applied: HubSpot Accept button contrast improved");
    }

    if (declineButton && !declineButton.hasAttribute("data-contrast-fixed")) {
      declineButton.style.color = "#000000";
      declineButton.style.backgroundColor = "#f0f0f0";
      declineButton.style.border = "1px solid #333333";
      declineButton.setAttribute("data-contrast-fixed", "true");

      log(
        "Accessibility fix applied: HubSpot Decline button contrast improved"
      );
    }

    // Fix Privacy Policy link contrast
    const privacyLinks = document.querySelectorAll(
      'a[href*="privacy-policy"], a[title*="Privacy Policy"]'
    );

    privacyLinks.forEach((link) => {
      if (!link.hasAttribute("data-contrast-fixed")) {
        // Only apply if it's part of HubSpot forms (check for parent .hs- classes)
        const isHubSpotElement = link.closest('[id^="hs-"], [class*="hs-"]');
        if (isHubSpotElement) {
          link.style.color = "#0066cc";
          link.style.textDecoration = "underline";
          link.setAttribute("data-contrast-fixed", "true");

          log(
            "Accessibility fix applied: Privacy Policy link contrast improved"
          );
        }
      }
    });

    // Fix required asterisk contrast
    const requiredSpans = document.querySelectorAll(".hs-form-required");

    requiredSpans.forEach((span) => {
      if (!span.hasAttribute("data-contrast-fixed")) {
        span.style.color = "#cc0000";
        span.setAttribute("data-contrast-fixed", "true");

        log("Accessibility fix applied: Required asterisk contrast improved");
      }
    });
  }

  /**
   * Adds proper landmark roles to semantic HTML5 elements
   */
  function fixLandmarkRoles() {
    // Fix header elements - should have role="banner" if they're the main page header
    const headers = document.querySelectorAll("header");
    headers.forEach((header) => {
      // Check if this is a top-level header (not nested in article, section, etc.)
      const isMainHeader =
        !header.closest("article, section, aside, nav") ||
        header.closest(".shopify-section--header");

      if (isMainHeader && !header.hasAttribute("role")) {
        header.setAttribute("role", "banner");

        log("Accessibility fix applied: Header element now has role='banner'");
      }
    });

    // Fix main elements - ensure they have role="main" (though HTML5 main should be implicit)
    const mainElements = document.querySelectorAll("main");
    mainElements.forEach((main) => {
      if (!main.hasAttribute("role")) {
        main.setAttribute("role", "main");

        log("Accessibility fix applied: Main element now has role='main'");
      }
    });

    // Fix navigation elements - very comprehensive detection
    // Look for various patterns that indicate navigation menus
    const potentialNavContainers = document.querySelectorAll(
      "header ul:not([role]):not([aria-hidden='true']), " +
        "header ol:not([role]):not([aria-hidden='true']), " +
        "header div.menu:not([role]), header div[class*='nav']:not([role]), " +
        "header div[class*='navigation']:not([role]), header div[class*='menu']:not([role]), " +
        "header div[id*='nav']:not([role]), header div[id*='menu']:not([role]), " +
        "ul[class*='menu']:not([role]), ul[class*='nav']:not([role]), " +
        "ol[class*='menu']:not([role]), ol[class*='nav']:not([role]), " +
        "div[class*='menu']:not([role]):not(.mega-menu), div[class*='nav']:not([role]), " +
        "div[id*='menu']:not([role]), div[id*='nav']:not([role]), " +
        ".header-menu:not([role]), .site-nav:not([role]), .main-menu:not([role])"
    );

    potentialNavContainers.forEach((container) => {
      // Skip if already inside a nav element
      const isInsideNav = container.closest("nav");

      // Check if truly hidden
      const computedStyle = window.getComputedStyle
        ? window.getComputedStyle(container)
        : null;
      const isHidden =
        (container.hasAttribute("aria-hidden") &&
          container.getAttribute("aria-hidden") === "true") ||
        (computedStyle &&
          (computedStyle.display === "none" ||
            computedStyle.visibility === "hidden"));

      if (!isInsideNav && !isHidden) {
        // Check if it contains navigation-like links
        const links = container.querySelectorAll("a");
        const linkCount = links.length;

        // Be more aggressive - determine if this is likely a navigation menu
        const hasNavClass = container.classList
          .toString()
          .match(/\b(menu|nav|navigation|site-nav|header-menu)\b/i);
        const hasNavId =
          container.id && container.id.match(/\b(menu|nav|navigation)\b/i);
        const isListInHeader =
          (container.tagName === "UL" || container.tagName === "OL") &&
          container.closest("header");
        const hasMultipleLinks = linkCount >= 2;
        const hasSingleLinkButNavClass =
          linkCount >= 1 && (hasNavClass || hasNavId);

        const isLikelyNav =
          hasMultipleLinks ||
          hasNavClass ||
          hasNavId ||
          isListInHeader ||
          hasSingleLinkButNavClass;

        if (isLikelyNav) {
          container.setAttribute("role", "navigation");

          // Add appropriate aria-label based on context
          let ariaLabel = "Navigation";
          if (container.closest("header")) {
            ariaLabel = "Main navigation";
          } else if (container.closest("footer")) {
            ariaLabel = "Footer navigation";
          } else if (
            container.classList.toString().match(/\bmobile/i) ||
            container.id.match(/\bmobile/i)
          ) {
            ariaLabel = "Mobile navigation";
          } else if (container.classList.toString().match(/\bsidebar/i)) {
            ariaLabel = "Sidebar navigation";
          }

          container.setAttribute("aria-label", ariaLabel);

          log(
            `Accessibility fix applied: Navigation container now has role='navigation' with aria-label='${ariaLabel}'`
          );
        }
      }
    });

    // Fix search forms - ensure they have role="search"
    const searchForms = document.querySelectorAll(
      'form[action*="/search"], form[action*="search"]'
    );

    searchForms.forEach((form) => {
      if (!form.hasAttribute("role")) {
        form.setAttribute("role", "search");

        log("Accessibility fix applied: Search form now has role='search'");
      }
    });

    // Fix footer elements - should have role="contentinfo"
    const footers = document.querySelectorAll("footer");
    footers.forEach((footer) => {
      // Check if this is a top-level footer (not nested)
      const isMainFooter =
        !footer.closest("article, section, aside") ||
        footer.closest(".shopify-section--footer");

      if (isMainFooter && !footer.hasAttribute("role")) {
        footer.setAttribute("role", "contentinfo");

        log(
          "Accessibility fix applied: Footer element now has role='contentinfo'"
        );
      }
    });

    // Fix any remaining list-based menus that might be missed
    // Target lists with multiple links that aren't already in nav or have roles
    const listMenus = document.querySelectorAll(
      "ul:not([role]), ol:not([role])"
    );
    listMenus.forEach((list) => {
      // Skip if already handled
      if (
        list.closest("nav") ||
        (list.hasAttribute("aria-hidden") &&
          list.getAttribute("aria-hidden") === "true")
      ) {
        return;
      }

      // Check if this list has navigation-like characteristics
      const directLinks = list.querySelectorAll(":scope > li > a");
      const allLinks = list.querySelectorAll("a");
      const hasMultipleLinks = directLinks.length >= 2 || allLinks.length >= 3;
      const hasNavClass =
        list.className &&
        list.className.match(/\b(menu|nav|navigation|site-nav|main-nav)\b/i);
      const hasNavId = list.id && list.id.match(/\b(menu|nav|navigation)\b/i);

      // Check if the list items are menu-like
      const listItems = list.querySelectorAll(":scope > li");
      const menuLikeItems = Array.from(listItems).filter((li) => {
        return li.querySelector("a");
      });
      const hasMenuStructure = menuLikeItems.length >= 2;

      // Check if in a header/footer context with links
      const inHeaderOrFooter = list.closest("header, footer");
      const inHeaderWithLinks = inHeaderOrFooter && allLinks.length >= 1;

      if (
        hasNavClass ||
        hasNavId ||
        (hasMultipleLinks && hasMenuStructure) ||
        inHeaderWithLinks
      ) {
        list.setAttribute("role", "navigation");

        // Determine context for aria-label
        let ariaLabel = "Navigation menu";
        if (list.closest("header")) {
          ariaLabel = "Header navigation menu";
        } else if (list.closest("footer")) {
          ariaLabel = "Footer navigation menu";
        } else if (list.closest("aside")) {
          ariaLabel = "Sidebar navigation menu";
        }

        list.setAttribute("aria-label", ariaLabel);

        log(
          `Accessibility fix applied: List-based menu now has role='navigation' with aria-label='${ariaLabel}'`
        );
      }
    });

    // Also check for any divs or sections that look like menus but weren't caught
    const additionalMenuContainers = document.querySelectorAll(
      "div:not([role])[class*='menu'], div:not([role])[id*='menu'], " +
        "div:not([role])[class*='nav'], div:not([role])[id*='nav'], " +
        "section:not([role])[class*='menu'], section:not([role])[class*='nav']"
    );

    additionalMenuContainers.forEach((container) => {
      // Skip if inside nav or has aria-hidden
      if (
        container.closest("nav") ||
        (container.hasAttribute("aria-hidden") &&
          container.getAttribute("aria-hidden") === "true")
      ) {
        return;
      }

      // Check if it has links that suggest it's a menu
      const links = container.querySelectorAll("a");
      if (links.length >= 2) {
        container.setAttribute("role", "navigation");

        let ariaLabel = "Navigation";
        if (container.closest("header")) {
          ariaLabel = "Header navigation";
        } else if (container.closest("footer")) {
          ariaLabel = "Footer navigation";
        }

        container.setAttribute("aria-label", ariaLabel);

        log(
          `Accessibility fix applied: Additional menu container now has role='navigation'`
        );
      }
    });
  }

  /**
   * Initialize the accessibility fixes
   */
  function init() {
    // Function to run all accessibility fixes
    function runAllFixes() {
      fixHeadingHierarchy();
      fixHeadingAccessibility();
      fixExternalLinkAccessibility();
      fixHubSpotNavigationRoles();
      fixRequiredFormFields();
      fixHubSpotContrast();
      fixLandmarkRoles();
    }

    // Run on DOM ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", runAllFixes);
    } else {
      // DOM is already ready, run immediately
      runAllFixes();
    }

    // Run again after a short delay to catch dynamically loaded content
    setTimeout(runAllFixes, 1000);
    setTimeout(runAllFixes, 3000);
    setTimeout(runAllFixes, 5000);

    // Set up periodic checks for dynamically added content (every 10 seconds)
    // This catches third-party content that loads slowly
    setInterval(runAllFixes, 10000);

    // Also observe for dynamically added content
    if (typeof MutationObserver !== "undefined") {
      const observer = new MutationObserver(function (mutations) {
        let shouldRerun = false;

        mutations.forEach(function (mutation) {
          // Check if new nodes were added
          if (mutation.addedNodes && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(function (node) {
              // Check if it's an element node
              if (node.nodeType === 1) {
                // Element node
                // Check for <p> tags with heading classes
                const hasHeadingP =
                  node.querySelector &&
                  (node.querySelector("p.h1, p.h2, p.h3, p.h4, p.h5, p.h6") ||
                    (node.tagName === "P" &&
                      /\bh[1-6]\b/.test(node.className)));

                // Check for links with target="_blank"
                const hasExternalLinks =
                  node.querySelector &&
                  (node.querySelector('a[target="_blank"], a[target="_new"]') ||
                    (node.tagName === "A" &&
                      (node.getAttribute("target") === "_blank" ||
                        node.getAttribute("target") === "_new")));

                // Check for HubSpot elements
                const hasHubSpotElements =
                  node.querySelector &&
                  (node.querySelector('[id^="hs-"], [class*="hs-"]') ||
                    (node.id && node.id.startsWith("hs-")) ||
                    (node.className && node.className.includes("hs-")));

                // Check for required form fields
                const hasRequiredFields =
                  node.querySelector &&
                  (node.querySelector(
                    "input[required], textarea[required], select[required]"
                  ) ||
                    ((node.tagName === "INPUT" ||
                      node.tagName === "TEXTAREA" ||
                      node.tagName === "SELECT") &&
                      node.hasAttribute("required")));

                // Check for landmark elements
                const hasLandmarkElements =
                  node.querySelector &&
                  (node.querySelector("header, main, footer, nav, form") ||
                    node.tagName === "HEADER" ||
                    node.tagName === "MAIN" ||
                    node.tagName === "FOOTER" ||
                    node.tagName === "NAV" ||
                    node.tagName === "FORM");

                if (
                  hasHeadingP ||
                  hasExternalLinks ||
                  hasHubSpotElements ||
                  hasRequiredFields ||
                  hasLandmarkElements
                ) {
                  shouldRerun = true;
                }
              }
            });
          }
        });

        if (shouldRerun) {
          runAllFixes();
        }
      });

      // Start observing the document for changes
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  // Start the script
  init();
})();
